/**
 * The import wizard's states, as components that take their facts as props.
 *
 * `ImportNew` and `ImportDetailScreen` own the queries and the mutations; nothing here calls
 * the network. A static render of these shows one frozen frame, so the two states defined by a
 * value that moves — `ImportChecking` and `ImportApplying` — are asserted for a frame and owe
 * the advance to U79.
 */
import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import {
  Button,
  DataTable,
  INITIAL_CURSOR,
  PlanGate,
  Surface,
  type Column,
  type DataTableRow,
  type TableCursor,
} from "@packagepage/ui";
import {
  IMPORT_MAX_BYTES,
  IMPORT_MAX_ROWS,
  correctedGtin14,
  type CompanyPrefixSummary,
  type ImportDateOrder,
  type ImportDetail,
  type ImportMapping,
  type ImportRowView,
  type Tier,
  DATE_ORDERS,
  permitsPublishing,
} from "@packagepage/shared";

import {
  GROUP_COPY,
  GROUP_OF,
  KIND_LABEL,
  filePrefixOf,
  summariseGroups,
  type ErrorGroup,
  type GroupSummary,
} from "./import-errors.js";
import { fieldOptions, fieldWords } from "./import-fields.js";
import {
  CANCELLABLE,
  DATE_ORDER_LABELS,
  IMPORT_TIER,
  STATUS_WORDS,
  STEP_LABELS,
  applyingProgress,
  checkingProgress,
  count,
  doneFigures,
  mapsADate,
  mappingFaults,
  overLimitFigures,
  percent,
  readMapping,
  readStats,
  reviewFigures,
  seedDateOrder,
  stepOf,
  upgradeTarget,
} from "./import-wizard.js";
import type { UploadPhase } from "../api/import-upload.js";

import "@packagepage/ui/surface.css";
import "@packagepage/ui/button.css";
import "@packagepage/ui/gate.css";
import "@packagepage/ui/table.css";
import "./import.css";

const MAX_MB = IMPORT_MAX_BYTES / (1024 * 1024);

/**
 * English, and it has to be right at one — "1 row", "is" not "are". The fourth copy of a
 * three-line helper (`checks.ts`, `attention.ts`, `trial-states.ts`); a fifth is the moment
 * to move it.
 */
const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

/** The one count figure every screen quotes: the caps, from the constants the API enforces. */
export const CAPS_SENTENCE = `CSV or XLSX · up to ${count(IMPORT_MAX_ROWS)} rows · ${String(MAX_MB)} MB`;

/** A count of problems. `warn`, never `error`: the file is not at fault and nothing has failed (`screens.md` §7). */
export function ImportCount({ n, label }: { n: number; label: string }): ReactNode {
  return (
    <Surface as="span" tone="warn" size="pill" data-import-count={label}>
      {count(n)}
    </Surface>
  );
}

export function ImportStepper({ status }: { status: ImportDetail["status"] }): ReactNode {
  const { current, complete } = stepOf(status);
  return (
    <ol className="pp-import__steps" aria-label="Import steps">
      {STEP_LABELS.map((label, index) => {
        const state = complete
          ? "done"
          : current === null
            ? "todo"
            : index < current
              ? "done"
              : index === current
                ? "current"
                : "todo";
        return (
          <li
            key={label}
            data-step-state={state}
            aria-current={state === "current" ? "step" : undefined}
          >
            {state === "done" ? <span aria-hidden="true">✓ </span> : null}
            {label}
          </li>
        );
      })}
    </ol>
  );
}

/** Title, facts, stepper and the one Cancel. */
export function ImportFrame({
  detail,
  onCancel,
  cancelling = false,
  children,
}: {
  detail: ImportDetail;
  onCancel?: () => void;
  cancelling?: boolean;
  children: ReactNode;
}): ReactNode {
  const stats = readStats(detail.stats);
  const facts = [
    detail.bytes === null ? null : `${(detail.bytes / (1024 * 1024)).toFixed(1)} MB`,
    stats.rows === undefined
      ? null
      : `${count(stats.rows)} ${plural(stats.rows, "row", "rows")}`,
    STATUS_WORDS[detail.status],
  ].filter((f): f is string => f !== null);
  return (
    <div
      className="pp-import"
      data-screen-label="Import"
      data-import-status={detail.status}
    >
      <header className="pp-import__header">
        <div>
          <h1 className="pp-import__title">{detail.filename}</h1>
          <p className="pp-import__meta">{facts.join(" · ")}</p>
        </div>
        {CANCELLABLE[detail.status] ? (
          <Button
            variant="quiet"
            data-import-cancel="true"
            loading={cancelling}
            onClick={onCancel}
          >
            Cancel this import
          </Button>
        ) : null}
      </header>
      <ImportStepper status={detail.status} />
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- A · upload

const PHASE_WORDS: Readonly<Record<UploadPhase, string>> = {
  hashing: "Reading your file…",
  creating: "Starting the import…",
  uploading: "Uploading…",
  confirming: "Checking the upload landed…",
};

export function ImportGate(): ReactNode {
  return (
    <div className="pp-import" data-screen-label="Import · gated">
      <h1 className="pp-import__title">Import a spreadsheet</h1>
      <PlanGate
        plan={IMPORT_TIER}
        feature="Spreadsheet import comes with Starter"
        why="An import checks every GTIN against your verified prefixes, reads the dates and units in each row, and keeps a record of what it did with every one. That work runs on our servers for as long as your file needs."
        instead="You can still add products one at a time on your current plan."
      />
    </div>
  );
}

export function ImportUpload({
  onPick,
  phase,
  refusal,
  templateName,
}: {
  onPick: (file: File) => void;
  phase: UploadPhase | null;
  refusal: string | null;
  templateName?: string;
}): ReactNode {
  const busy = phase !== null;
  return (
    <div className="pp-import" data-screen-label="Import · upload">
      <h1 className="pp-import__title">Import a spreadsheet</h1>
      <ImportStepper status="uploaded" />
      <div
        className="pp-import__drop"
        data-busy={busy ? "true" : undefined}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const file = event.dataTransfer.files[0];
          if (file !== undefined && !busy) onPick(file);
        }}
      >
        <p className="pp-import__drop-title">
          {phase === null ? "Drop your spreadsheet here" : PHASE_WORDS[phase]}
        </p>
        <p className="pp-import__drop-caps">{CAPS_SENTENCE}</p>
        <label className="pp-button" data-variant="primary" aria-disabled={busy}>
          Choose a file
          <input
            type="file"
            className="pp-import__file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file !== undefined) onPick(file);
            }}
          />
        </label>
      </div>
      {refusal === null ? null : (
        <Surface tone="warn" emphasis="filled">
          <p role="alert">{refusal}</p>
        </Surface>
      )}
      {templateName === undefined ? null : (
        <p className="pp-import__template">
          Using the same column layout as last time? Your saved mapping “{templateName}”
          will be applied automatically.
        </p>
      )}
      <ul className="pp-import__sources" aria-label="Where the file comes from">
        <li data-source="spreadsheet" data-live="true">
          Spreadsheet
        </li>
        <li data-source="shopify" data-live="false" aria-disabled="true">
          Shopify · soon
        </li>
      </ul>
    </div>
  );
}

// ------------------------------------------------- the wait before B, and C

/** The worker is still reading the file. Not one of the eight states: it has no figures yet. */
export function ImportReading({ detail }: { detail: ImportDetail }): ReactNode {
  return (
    <Surface>
      <div data-screen-label="Import · reading">
        <h2 className="pp-import__heading">Reading your file</h2>
        <p>
          We are reading {detail.filename} and matching its columns to our fields. This
          step only reads.
        </p>
        <p>Close the tab if you need to. Coming back picks up where it stopped.</p>
      </div>
    </Surface>
  );
}

function Bar({
  done,
  total,
  fraction,
  label,
}: {
  done: number;
  total: number;
  fraction: number;
  label: string;
}): ReactNode {
  return (
    <div
      className="pp-import__bar"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      data-percent={percent(fraction)}
    >
      <div
        className="pp-import__bar-fill"
        style={{ width: `${String(fraction * 100)}%` }}
      />
    </div>
  );
}

/** State C. One frame at a given `validatedRowNo`; that the bar advances and the poll stops is U79's. */
export function ImportChecking({ detail }: { detail: ImportDetail }): ReactNode {
  const p = checkingProgress(detail);
  return (
    <Surface>
      <div data-screen-label="Import · checking">
        <h2 className="pp-import__heading">Checking your file</h2>
        <Bar {...p} label="Rows checked" />
        <p className="pp-import__figure">
          <strong>{count(p.done)}</strong> / {count(p.total)}
        </p>
        <p>Nothing has been changed in your account yet — this step only reads.</p>
        <p>Close the tab if you need to. Coming back picks up where it stopped.</p>
      </div>
    </Surface>
  );
}

// ------------------------------------------------------------ B · mapping

export interface ConfirmedMapping {
  readonly mapping: { readonly columns: ImportMapping["columns"] };
  readonly dateOrder: ImportDateOrder | null;
  readonly saveAs?: string;
}

export function ImportMatching({
  detail,
  onConfirm,
  pending = false,
  refusal = null,
}: {
  detail: ImportDetail;
  onConfirm: (input: ConfirmedMapping) => void;
  pending?: boolean;
  refusal?: string | null;
}): ReactNode {
  const proposal = readMapping(detail.mapping);
  const stats = readStats(detail.stats);
  const columns = proposal?.columns ?? [];
  const [fields, setFields] = useState<(string | null)[]>(columns.map((c) => c.field));
  const [dateOrder, setDateOrder] = useState<ImportDateOrder | null>(
    seedDateOrder(proposal, detail.template),
  );
  const [save, setSave] = useState(false);
  const [saveName, setSaveName] = useState("");

  const options = fieldOptions(fields);
  const matched = columns.filter((c) => c.field !== null).length;
  const faults = mappingFaults(fields);
  const dated = mapsADate(fields);

  return (
    <Surface>
      <div data-screen-label="Import · match columns">
        <h2 className="pp-import__heading">Match your columns</h2>
        {detail.template === null ? null : (
          <p className="pp-import__template">
            Started from your saved mapping “{detail.template.name}”.
          </p>
        )}
        <div
          className="pp-import__map"
          role="table"
          aria-label="Your columns and our fields"
        >
          <div className="pp-import__map-head" role="row">
            <span role="columnheader">Your column</span>
            <span role="columnheader">Our field</span>
          </div>
          {columns.map((column, index) => {
            const field = fields[index] ?? null;
            const reason = stats.notImported?.[column.header];
            return (
              <div className="pp-import__map-row" role="row" key={column.header}>
                <span role="cell" className="pp-import__map-source">
                  {column.header}
                </span>
                <span role="cell">
                  <select
                    aria-label={`Field for the column ${column.header}`}
                    value={field ?? ""}
                    onChange={(event) =>
                      setFields((current) =>
                        current.map((f, i) =>
                          i === index ? event.target.value || null : f,
                        ),
                      )
                    }
                  >
                    <option value="">Not imported</option>
                    {options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {field === null ? (
                    <span className="pp-import__map-note" data-unmatched="true">
                      Not imported · {reason ?? "no field of ours holds this column"}
                    </span>
                  ) : fieldWords(field).term === null ? null : (
                    <span className="pp-import__map-term">{fieldWords(field).term}</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <p>
          {count(matched)} of {count(columns.length)}{" "}
          {plural(columns.length, "column", "columns")} matched automatically.
        </p>

        {dated ? (
          <p>
            <label>
              How does this file write dates?{" "}
              <select
                aria-label="Date order"
                value={dateOrder ?? ""}
                onChange={(event) =>
                  setDateOrder(DATE_ORDERS.find((d) => d === event.target.value) ?? null)
                }
              >
                <option value="">Not chosen</option>
                {DATE_ORDERS.map((order) => (
                  <option key={order} value={order}>
                    {DATE_ORDER_LABELS[order]}
                  </option>
                ))}
              </select>
            </label>
          </p>
        ) : null}

        <p>
          <label>
            <input
              type="checkbox"
              checked={save}
              onChange={(event) => setSave(event.target.checked)}
            />{" "}
            Save as a template
          </label>{" "}
          <input
            type="text"
            aria-label="Template name"
            placeholder="Template name"
            maxLength={120}
            value={saveName}
            disabled={!save}
            onChange={(event) => setSaveName(event.target.value)}
          />
        </p>

        {faults.blocking === null ? null : (
          <p role="alert" data-fault="blocking">
            {faults.blocking}
          </p>
        )}
        {faults.notes.map((note) => (
          <p key={note} data-fault="note">
            {note}
          </p>
        ))}
        {refusal === null ? null : (
          <Surface tone="warn" emphasis="filled">
            <p role="alert">{refusal}</p>
          </Surface>
        )}

        <p>
          <Button
            variant="primary"
            loading={pending}
            disabled={faults.blocking !== null}
            onClick={() =>
              onConfirm({
                mapping: {
                  columns: columns.map((c, i) => ({
                    header: c.header,
                    field: fields[i] ?? null,
                  })),
                },
                dateOrder: dated ? dateOrder : null,
                ...(save && saveName.trim() !== "" ? { saveAs: saveName.trim() } : {}),
              })
            }
          >
            Check my file
          </Button>
        </p>
      </div>
    </Surface>
  );
}

// ----------------------------------------------- review: D, E and H share it

export interface WorkspaceChoice {
  readonly id: string;
  readonly name: string;
}

function WorkspacePicker({
  workspaces,
  value,
  onChange,
}: {
  workspaces: readonly WorkspaceChoice[];
  value: string;
  onChange: (id: string) => void;
}): ReactNode {
  if (workspaces.length < 2) return null;
  return (
    <p>
      <label>
        File the new products in{" "}
        <select
          aria-label="Workspace"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </label>
    </p>
  );
}

export interface ApplyChoice {
  readonly workspaceId: string;
  readonly limit?: number;
}

/** State D. One card, one button, nothing else. */
export function ImportClean({
  detail,
  workspaces,
  onApply,
  applying = false,
}: {
  detail: ImportDetail;
  workspaces: readonly WorkspaceChoice[];
  onApply: (choice: ApplyChoice) => void;
  applying?: boolean;
}): ReactNode {
  const f = reviewFigures(detail);
  const [workspaceId, setWorkspaceId] = useState(workspaces[0]?.id ?? "");
  return (
    <Surface>
      <div data-screen-label="Import · clean file">
        <p className="pp-import__eyebrow">Clean file</p>
        <h2 className="pp-import__display">
          All {count(f.rows)} {plural(f.rows, "row", "rows")} checked out
        </h2>
        <p>
          Every GTIN is valid, inside your prefix, and unique. {count(f.create)} new{" "}
          {f.create === 1 ? "product" : "products"},{" "}
          {f.update === 0
            ? "no updates"
            : `${count(f.update)} ${f.update === 1 ? "update" : "updates"}`}
          .
        </p>
        <WorkspacePicker
          workspaces={workspaces}
          value={workspaceId}
          onChange={setWorkspaceId}
        />
        <p>
          <Button
            variant="primary"
            loading={applying}
            disabled={workspaceId === ""}
            onClick={() => onApply({ workspaceId })}
          >
            Apply all {count(f.ready)}
          </Button>
        </p>
        <p>New products arrive as drafts — nothing publishes until you say so.</p>
      </div>
    </Surface>
  );
}

/** State E. The arithmetic first, the subset first of the buttons, and the price read from `PLANS`. */
export function ImportOverLimit({
  detail,
  plan,
  workspaces,
  onApply,
  applying = false,
}: {
  detail: ImportDetail;
  plan: Tier;
  workspaces: readonly WorkspaceChoice[];
  onApply: (choice: ApplyChoice) => void;
  applying?: boolean;
}): ReactNode {
  const [workspaceId, setWorkspaceId] = useState(workspaces[0]?.id ?? "");
  if (detail.overLimit === null) return null;
  const f = overLimitFigures(detail.overLimit);
  const target = upgradeTarget(plan);
  const ready = reviewFigures(detail).ready;
  const label = (tier: Tier) => tier.charAt(0).toUpperCase() + tier.slice(1);
  return (
    <Surface>
      <div data-screen-label="Import · over limit">
        <h2 className="pp-import__heading">
          This import adds {count(f.newProducts)}{" "}
          {plural(f.newProducts, "product", "products")}. Your plan has{" "}
          {count(Math.max(0, f.slotsLeft))}{" "}
          {plural(Math.max(0, f.slotsLeft), "slot", "slots")} left.
        </h2>
        <ul className="pp-import__sum" aria-label="The arithmetic">
          <li data-sum="allowance">
            {label(plan)} includes <strong>{count(f.allowance)}</strong>
          </li>
          <li data-sum="published">
            Already using <strong>{count(f.published)}</strong>
          </li>
          <li data-sum="short">
            Short by <ImportCount n={f.shortBy} label="short-by" />
          </li>
        </ul>
        <p>
          {target === null ? null : (
            <>
              {label(target.tier)} covers {count(target.activeGtins)} for $
              {count(target.monthlyUsd)}.{" "}
            </>
          )}
          {f.applyNow === null
            ? "No slot is left to apply into."
            : `Or apply the first ${count(f.applyNow)} now — the file stays here, and the remaining ${count(f.remaining)} ${plural(f.remaining, "row is", "rows are")} one click away whenever you have room.`}
        </p>
        <WorkspacePicker
          workspaces={workspaces}
          value={workspaceId}
          onChange={setWorkspaceId}
        />
        <p className="pp-import__actions">
          {f.applyNow === null ? null : (
            <Button
              variant="primary"
              loading={applying}
              disabled={workspaceId === ""}
              onClick={() => onApply({ workspaceId, limit: f.applyNow ?? 1 })}
            >
              Apply {count(f.applyNow)} now
            </Button>
          )}
          {target === null ? null : (
            <Link to="/settings/billing" className="pp-button" data-variant="secondary">
              Move to {label(target.tier)}
            </Link>
          )}
          <Button
            variant="quiet"
            loading={applying}
            disabled={workspaceId === ""}
            onClick={() => onApply({ workspaceId })}
          >
            Apply all {count(ready)}
          </Button>
        </p>
        <p className="pp-import__meta">
          These are drafts, and drafts don&apos;t count against your plan. The limit
          applies when you publish them.
        </p>
      </div>
    </Surface>
  );
}

// ------------------------------------------------------------------ H

interface ErrRow extends DataTableRow {
  readonly id: string;
  readonly view: ImportRowView;
  readonly gtin: string;
  readonly product: string;
  readonly problem: string;
}

/** What the table needs to find a row's own cells. */
export interface ColumnHeaders {
  readonly gtin: string | null;
  readonly product: string | null;
}

export function headersOf(mapping: ImportMapping | null): ColumnHeaders {
  const columns = mapping?.columns ?? [];
  return {
    gtin: columns.find((c) => c.field === "gtin14")?.header ?? null,
    product:
      columns.find((c) => c.field?.startsWith("name."))?.header ??
      columns.find((c) => c.field === "brandName")?.header ??
      null,
  };
}

function cell(row: ImportRowView, header: string | null): string {
  return header === null ? "" : (row.raw[header] ?? "");
}

/** Every row's full sentence, opened in place. Opening a row has no destination of its own. */
export function RowPanel({
  row,
  headers,
}: {
  row: ImportRowView;
  headers: ColumnHeaders;
}): ReactNode {
  return (
    <Surface tone="neutral" emphasis="filled">
      <div data-open-panel={row.rowNo}>
        <p>
          <strong>Row {count(row.rowNo)}</strong>
        </p>
        <ul>
          {row.errors.map((e) => (
            <li key={`${e.kind}:${e.detail}`}>{e.detail}</li>
          ))}
        </ul>
        <dl className="pp-import__cells">
          {Object.entries(row.raw).map(([header, value]) => (
            <div
              key={header}
              data-gtin-cell={header === headers.gtin ? "true" : undefined}
            >
              <dt>{header}</dt>
              <dd>{value === "" ? "—" : value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Surface>
  );
}

/**
 * The error table: a `DataTable` with `cursor` and `onOpen`, plus a button in the primary cell
 * for the mouse. Each prop closes one row of the capability census in the Dev Log.
 */
export function ErrorTable({
  group,
  rows,
  headers,
}: {
  group: ErrorGroup;
  rows: readonly ImportRowView[];
  headers: ColumnHeaders;
}): ReactNode {
  const [cursor, setCursor] = useState<TableCursor>(INITIAL_CURSOR);
  const [open, setOpen] = useState<string | null>(null);
  const data: ErrRow[] = rows.map((view) => ({
    id: String(view.rowNo),
    view,
    gtin: cell(view, headers.gtin),
    product: cell(view, headers.product),
    problem:
      view.errors
        .filter((e) => GROUP_OF[e.kind] === group)
        .map((e) => KIND_LABEL[e.kind])[0] ?? "",
  }));
  const columns: ReadonlyArray<Column<ErrRow>> = [
    {
      key: "row",
      header: "Row",
      width: 130,
      primary: true,
      render: (row) => (
        <button
          type="button"
          className="pp-import__open"
          data-open-row={row.id}
          onClick={() => setOpen(row.id)}
        >
          Row {count(row.view.rowNo)}
        </button>
      ),
    },
    {
      key: "gtin",
      header: "As supplied",
      width: 190,
      render: (row) => <span className="pp-import__mono">{row.gtin || "—"}</span>,
    },
    {
      key: "product",
      header: "Product",
      width: 260,
      render: (row) => row.product || "—",
    },
    ...(group === "check_digit"
      ? [
          {
            key: "should",
            header: "Should be",
            width: 210,
            render: (row: ErrRow) => (
              <span className="pp-import__mono" data-should-be="true">
                {correctedGtin14(row.gtin) ?? "—"}
              </span>
            ),
          },
        ]
      : [
          {
            key: "problem",
            header: "Problem",
            width: 320,
            render: (row: ErrRow) => row.problem,
          },
        ]),
  ];
  const opened = data.find((r) => r.id === open);
  return (
    <>
      <DataTable
        columns={columns}
        rows={data}
        caption={GROUP_COPY[group].title}
        cursor={cursor}
        onCursorChange={setCursor}
        onOpen={(id) => setOpen(id)}
      />
      {opened === undefined ? null : <RowPanel row={opened.view} headers={headers} />}
    </>
  );
}

function exampleOf(
  group: ErrorGroup,
  row: ImportRowView,
  headers: ColumnHeaders,
): string {
  const rowNo = `Row ${count(row.rowNo)}`;
  if (group === "values") {
    const first = row.errors.find((e) => GROUP_OF[e.kind] === group);
    return first === undefined ? rowNo : `${rowNo}: ${first.detail}`;
  }
  const said = cell(row, headers.gtin);
  return said === "" ? rowNo : `${rowNo} says ${said}.`;
}

function prefixSentence(
  summary: GroupSummary,
  rows: readonly ImportRowView[],
  headers: ColumnHeaders,
  prefixes: readonly CompanyPrefixSummary[],
): string {
  const verified = prefixes
    .filter((p) => permitsPublishing(p.verification))
    .map((p) => p.prefix);
  const first = rows[0];
  const file = first === undefined ? null : filePrefixOf(cell(first, headers.gtin));
  const own =
    verified.length === 0
      ? "you have no verified prefix yet"
      : `your verified ${verified.length === 1 ? "prefix is" : "prefixes are"} ${verified.join(", ")}`;
  return file === null
    ? `${count(summary.total)} ${plural(summary.total, "row is", "rows are")} outside a prefix you have verified, and ${own}.`
    : `${count(summary.total)} ${plural(summary.total, "row starts", "rows start")} with ${file}, and ${own}.`;
}

export function ErrorGroupSection({
  summary,
  rows,
  headers,
  prefixes,
}: {
  summary: GroupSummary;
  rows: readonly ImportRowView[];
  headers: ColumnHeaders;
  prefixes: readonly CompanyPrefixSummary[];
}): ReactNode {
  const copy = GROUP_COPY[summary.group];
  const first = rows[0];
  return (
    <Surface>
      <section data-group={summary.group} aria-label={copy.title}>
        <h3 className="pp-import__group-title">
          {copy.title} <ImportCount n={summary.total} label={`group-${summary.group}`} />
        </h3>
        <ul className="pp-import__kinds">
          {summary.kinds.map((k) => (
            <li key={k.kind} data-kind={k.kind}>
              {KIND_LABEL[k.kind]} · <ImportCount n={k.count} label={`kind-${k.kind}`} />
            </li>
          ))}
        </ul>
        <p data-part="rule">{copy.rule}</p>
        {first === undefined ? null : (
          <p data-part="example">{exampleOf(summary.group, first, headers)}</p>
        )}
        {summary.group === "prefix" ? (
          <p data-part="prefixes">{prefixSentence(summary, rows, headers, prefixes)}</p>
        ) : null}
        <p data-part="cause">{copy.cause}</p>
        <p data-part="fix">{copy.fix}</p>
        {summary.group === "check_digit" ? (
          <p data-resolution="skip">
            <strong>Skip these rows.</strong> They are left out of this import. Nothing
            here changes a GTIN.
          </p>
        ) : null}
        {rows.length === 0 ? null : (
          <>
            <ErrorTable group={summary.group} rows={rows} headers={headers} />
            <p className="pp-import__meta">
              Showing {count(rows.length)} of {count(summary.total)}. The rest are in the
              file you download. Row numbers match your spreadsheet exactly.
            </p>
          </>
        )}
      </section>
    </Surface>
  );
}

function Tiles({ detail }: { detail: ImportDetail }): ReactNode {
  const f = reviewFigures(detail);
  return (
    <ul className="pp-import__tiles" aria-label="What the file holds">
      <li data-tile="new">
        <span>New</span> <strong>{count(f.create)}</strong>
      </li>
      <li data-tile="updated">
        <span>Updated</span> <strong>{count(f.update)}</strong>
      </li>
      <li data-tile="unchanged">
        <span>Unchanged</span> <strong>{count(f.skip)}</strong>
      </li>
      <li data-tile="cant-accept">
        <span>Can&apos;t accept</span> <ImportCount n={f.errors} label="cant-accept" />
      </li>
    </ul>
  );
}

/** State H, the main screen: the rows that worked first, the causes grouped, the CSV as the road. */
export function ImportReview({
  detail,
  samples,
  prefixes,
  workspaces,
  onApply,
  onDownload,
  applying = false,
  downloading = false,
}: {
  detail: ImportDetail;
  samples: Readonly<Record<ErrorGroup, readonly ImportRowView[]>>;
  prefixes: readonly CompanyPrefixSummary[];
  workspaces: readonly WorkspaceChoice[];
  onApply: (choice: ApplyChoice) => void;
  onDownload: () => void;
  applying?: boolean;
  downloading?: boolean;
}): ReactNode {
  const f = reviewFigures(detail);
  const stats = readStats(detail.stats);
  const groups = summariseGroups(stats.byKind);
  const headers = headersOf(readMapping(detail.mapping));
  const [workspaceId, setWorkspaceId] = useState(workspaces[0]?.id ?? "");
  return (
    <div data-screen-label="Import · review" className="pp-import__review">
      <Surface>
        <div>
          <h2 className="pp-import__display">
            {count(f.ready)} {plural(f.ready, "row is", "rows are")} ready to go
          </h2>
          <p>
            That&apos;s {String(f.readyPercent)}% of the file. {count(f.errors)}{" "}
            {plural(f.errors, "row has", "rows have")} something we can&apos;t accept —{" "}
            {plural(f.errors, "it is", "they're")} grouped by cause below, and{" "}
            {plural(f.errors, "it does not stop", "none of them stop")} the other{" "}
            {count(f.ready)} from being applied.
          </p>
          <Tiles detail={detail} />
        </div>
      </Surface>

      <Surface>
        <div data-part="fix-in-spreadsheet">
          <h3 className="pp-import__group-title">Fix these in your spreadsheet</h3>
          <p>
            Download the {count(f.errors)}{" "}
            {plural(f.errors, "row as it arrived", "rows as they arrived")}, with a column
            saying what&apos;s wrong with each.{" "}
            {plural(f.errors, "Fix it there", "Fix them there")} and upload the file
            again. It holds only {plural(f.errors, "that row", "those rows")}, so only{" "}
            {plural(f.errors, "that row is", "those rows are")} checked.
          </p>
          <p>
            <Button variant="secondary" loading={downloading} onClick={onDownload}>
              Download {count(f.errors)} {plural(f.errors, "row", "rows")} to fix
            </Button>
          </p>
          <p>
            This is the intended route, not a consolation prize — it&apos;s faster than
            fixing {count(f.errors)} {plural(f.errors, "row", "rows")} in a browser.
          </p>
        </div>
      </Surface>

      <h2 className="pp-import__heading">
        What went wrong{" "}
        <span className="pp-import__meta">
          {count(groups.length)} {groups.length === 1 ? "cause" : "causes"}
        </span>
      </h2>
      {groups.map((summary) => (
        <ErrorGroupSection
          key={summary.group}
          summary={summary}
          rows={samples[summary.group]}
          headers={headers}
          prefixes={prefixes}
        />
      ))}

      <Surface>
        <div data-part="apply-bar">
          <WorkspacePicker
            workspaces={workspaces}
            value={workspaceId}
            onChange={setWorkspaceId}
          />
          <p>
            <Button
              variant="primary"
              loading={applying}
              disabled={f.ready === 0 || workspaceId === ""}
              onClick={() => onApply({ workspaceId })}
            >
              Apply {count(f.ready)} {plural(f.ready, "row", "rows")}
            </Button>
          </p>
          <p>
            {count(f.create)} new {f.create === 1 ? "product" : "products"},{" "}
            {count(f.update)} updated. New products arrive as drafts — nothing publishes
            until you say so.
          </p>
        </div>
      </Surface>
    </div>
  );
}

// ------------------------------------------------------------------ F, G

/** State F. One frame at a given `stats.applied`; that failures appear as they happen is U79's. */
export function ImportApplying({ detail }: { detail: ImportDetail }): ReactNode {
  const p = applyingProgress(detail);
  return (
    <Surface>
      <div data-screen-label="Import · applying">
        <h2 className="pp-import__heading">Applying</h2>
        <Bar {...p} label="Rows applied" />
        <p className="pp-import__figure">
          <strong>{count(p.done)}</strong> / {count(p.total)}
        </p>
        {p.failed === 0 ? null : (
          <Surface tone="neutral" emphasis="filled">
            <p data-part="failures">
              <ImportCount n={p.failed} label="apply-failed" />{" "}
              {p.failed === 1 ? "row" : "rows"} failed while applying. Everything else is
              going in, and the report at the end names each one.
            </p>
          </Surface>
        )}
        <p>
          Rows already applied stay applied. You can close the tab; coming back picks up
          where it stopped.
        </p>
      </div>
    </Surface>
  );
}

/** State G. Counts, the two next actions, and the mail sentence. */
export function ImportDone({
  detail,
  onDownload,
  downloading,
}: {
  detail: ImportDetail;
  onDownload: (rows: "failed" | undefined) => void;
  downloading?: boolean;
}): ReactNode {
  const f = doneFigures(detail);
  return (
    <Surface>
      <div data-screen-label="Import · done">
        <h2 className="pp-import__display">
          {count(f.applied)} {plural(f.applied, "row", "rows")} applied
        </h2>
        <dl className="pp-import__counts">
          {f.created === null ? null : (
            <div data-count="created">
              <dt>New products, as drafts</dt>
              <dd>{count(f.created)}</dd>
            </div>
          )}
          {f.updated === null ? null : (
            <div data-count="updated">
              <dt>Existing products updated</dt>
              <dd>{count(f.updated)}</dd>
            </div>
          )}
          {f.notApplied === 0 ? null : (
            <div data-count="not-applied">
              <dt>Rows left out by the limit you chose</dt>
              <dd>{count(f.notApplied)}</dd>
            </div>
          )}
          <div data-count="to-fix">
            <dt>Rows to fix and re-upload</dt>
            <dd>
              <ImportCount n={f.toFix + f.failed} label="to-fix" />
            </dd>
          </div>
        </dl>
        <p>
          New products are drafts. Nothing answers a scan until you publish one from its
          own page.
        </p>
        <p className="pp-import__actions">
          {f.created === null || f.created === 0 ? (
            // A null count cannot promise a draft exists, and the filtered list would be
            // the dead end `acceptance-tests.md` forbids.
            <Link to="/products" className="pp-button" data-variant="primary">
              Open your products
            </Link>
          ) : (
            <Link
              to="/products"
              search={{ status: "draft" }}
              className="pp-button"
              data-variant="primary"
              data-review-drafts="true"
            >
              Review {count(f.created)} {plural(f.created, "draft", "drafts")}
            </Link>
          )}
          {f.toFix > 0 ? (
            <Button
              variant="secondary"
              loading={downloading ?? false}
              onClick={() => onDownload(undefined)}
            >
              Download {count(f.toFix)} {plural(f.toFix, "row", "rows")} to fix
            </Button>
          ) : null}
          {f.failed > 0 ? (
            <Button
              variant="secondary"
              loading={downloading ?? false}
              onClick={() => onDownload("failed")}
            >
              Download {count(f.failed)}{" "}
              {plural(f.failed, "row that could not", "rows that could not")} be applied
            </Button>
          ) : null}
        </p>
        <p className="pp-import__meta">
          We emailed this summary to whoever applied the import. It stays in your{" "}
          <Link to="/imports">import history</Link>.
        </p>
      </div>
    </Surface>
  );
}

/** `failed` and `cancelled`. Not states of the mock: an import can end in either and the screen has to say so. */
export function ImportEnded({ detail }: { detail: ImportDetail }): ReactNode {
  const failed = detail.status === "failed";
  return (
    <Surface>
      <div data-screen-label={failed ? "Import · stopped" : "Import · cancelled"}>
        <h2 className="pp-import__heading">
          {failed ? "This import stopped" : "This import was cancelled"}
        </h2>
        <p>
          {failed
            ? (detail.error ?? "It stopped before it finished.")
            : "Nothing from it was added to your catalogue."}
        </p>
        <p>
          <Link to="/imports/new" className="pp-button" data-variant="primary">
            Start a new import
          </Link>
        </p>
      </div>
    </Surface>
  );
}
